import "@/App.css";
import React, { useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { MusicPlayerProvider } from "./MusicPlayerContext";
import { MobileBottomNav, ScrollToTop } from "./components/AppNav";
import AppUpdateBanner from "./components/AppUpdateBanner";
import ConventionBanner from "./components/ConventionBanner";
import Footer from "./components/Footer";
import GrowUnionsCard from "./components/GrowUnionsCard";
import JailCampaignCard from "./components/JailCampaignCard";
import { JuneteenthBanner } from "./components/JuneteenthBanner";
import { MiniPlayer } from "./components/MiniPlayer";
import { Navbar } from "./components/Navbar";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PartnersBar from "./components/PartnersBar";
import { SplashScreen } from "./components/SplashScreen";
import { FeaturedEventsBanner } from "./components/FeaturedEventsBanner";
import { AICourseBanner } from "./components/AICourseBanner";
import { DailyStreakCard } from "./components/DailyStreakCard";
import { recordDailyActivity } from "./lib/streak";
import { AuthCallback, AuthProvider } from "./contexts/AuthContext";
import { LandingPage } from "./pages/LandingPage";

// Lazy helper for named exports
const ln = (loader, name) => lazy(async () => ({ default: (await loader())[name] }));

// Lazy components (named exports)
const BookAdmin = ln(() => import("./components/BookAdmin"), "BookAdmin");
const BookPage = ln(() => import("./components/BookPage"), "BookPage");
const GalleryPage = ln(() => import("./components/GalleryPage"), "GalleryPage");
const MediaPage = ln(() => import("./components/MediaPage"), "MediaPage");
const PrintShopPage = ln(() => import("./components/PrintShopPage"), "PrintShopPage");
const ScholarshipsPage = ln(() => import("./components/ScholarshipsPage"), "ScholarshipsPage");
const StrikeTracker = ln(() => import("./components/StrikeTracker"), "StrikeTracker");

// Lazy pages (default exports)
const AFGEPage = lazy(() => import("./pages/AFGEPage"));
const AFSCMEPage = lazy(() => import("./pages/AFSCMEPage"));
const APALAPage = lazy(() => import("./pages/APALAPage"));
const APRIPage = lazy(() => import("./pages/APRIPage"));
const BCTGM2026Convention = lazy(() => import("./pages/BCTGM2026Convention"));
const BCTGMInternational = lazy(() => import("./pages/BCTGMInternational"));
const BackToWorkAdmin = lazy(() => import("./pages/BackToWorkAdmin"));
const BackToWorkPage = lazy(() => import("./pages/BackToWorkPage"));
const RegionalOneEmployeesPage = lazy(() => import("./pages/RegionalOneEmployeesPage"));
const RegionalOnePatientsPage = lazy(() => import("./pages/RegionalOnePatientsPage"));
const LibrariesPage = lazy(() => import("./pages/LibrariesPage"));
const FreeMiracleArticlePage = lazy(() => import("./pages/FreeMiracleArticlePage"));
const OneShelbyStoryPage = lazy(() => import("./pages/OneShelbyStoryPage"));
const BlueprintReading = lazy(() => import("./pages/BlueprintReading"));
const BuildingBetterJailPage = lazy(() => import("./pages/BuildingBetterJailPage"));
const HistoryAtTheTablePage = lazy(() => import("./pages/HistoryAtTheTablePage"));
const CBTUPage = lazy(() => import("./pages/CBTUPage"));
const CLUWPage = lazy(() => import("./pages/CLUWPage"));
const CareerPathwaysAdmin = lazy(() => import("./pages/CareerPathwaysAdmin"));
const CareerPathwaysPage = lazy(() => import("./pages/CareerPathwaysPage"));
const CertificatePage = lazy(() => import("./pages/CertificatePage"));
const CivicPowerPlaybookRedirect = lazy(() => import("./pages/CivicPowerPlaybookRedirect"));
const CivilRightsMuseumAdmin = lazy(() => import("./pages/CivilRightsMuseumAdmin"));
const CivilRightsMuseumPage = lazy(() => import("./pages/CivilRightsMuseumPage"));
const CommunityUnityCouncilPage = lazy(() => import("./pages/CommunityUnityCouncilPage"));
const Convention30 = lazy(() => import("./pages/Convention30"));
const CredibilityBarPreview = lazy(() => import("./pages/CredibilityBarPreview"));
const DeleteMyData = lazy(() => import("./pages/DeleteMyData"));
const DrLawson = lazy(() => import("./pages/DrLawson"));
const EndGroceryTaxPage = lazy(() => import("./pages/EndGroceryTaxPage"));
const DataCentersPage = lazy(() => import("./pages/DataCentersPage"));
const ClcDataCenterFlyerPage = lazy(() => import("./pages/ClcDataCenterFlyerPage"));
const WorkforceLaborBriefPage = lazy(() => import("./pages/WorkforceLaborBriefPage"));
const WorkforceCasePage = lazy(() => import("./pages/WorkforceCasePage"));
const UnknownRoute = lazy(() => import("./pages/UnknownRoute"));
const BusinessHubPage = lazy(() => import("./pages/BusinessHubPage"));
const SecondChanceHiringPage = lazy(() => import("./pages/SecondChanceHiringPage"));
const LincolnAlumniPage = lazy(() => import("./pages/LincolnAlumniPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NegotiationSchool = lazy(() => import("./pages/NegotiationSchool"));
const ArbitrationSchool = lazy(() => import("./pages/ArbitrationSchool"));
const GrievanceWritingSchool = lazy(() => import("./pages/GrievanceWritingSchool"));
const OrganizingSchool = lazy(() => import("./pages/OrganizingSchool"));
const AIWorkersSchool = lazy(() => import("./pages/AIWorkersSchool"));
const LaborEducationAcademy = lazy(() => import("./pages/LaborEducationAcademy"));
const EndorsementsPage = lazy(() => import("./pages/EndorsementsPage"));
const EquityAlliancePage = lazy(() => import("./pages/EquityAlliancePage"));
const NAACPPage = lazy(() => import("./pages/NAACPPage"));
const NAACPDesotoPage = lazy(() => import("./pages/NAACPDesotoPage"));
const FairPriceAdmin = lazy(() => import("./pages/FairPriceAdmin"));
const FairPriceWatch = lazy(() => import("./pages/FairPriceWatch"));
const FinancialLiteracyPage = lazy(() => import("./pages/FinancialLiteracyPage"));
const FourPillars = lazy(() => import("./pages/FourPillars"));
const FriendsOfLaborAdmin = lazy(() => import("./pages/FriendsOfLaborAdmin"));
const FriendsOfLaborPage = lazy(() => import("./pages/FriendsOfLaborPage"));
const MSFriendsOfLaborPage = lazy(() => import("./pages/MSFriendsOfLaborPage"));
const GrantSignAdmin = lazy(() => import("./pages/GrantSignAdmin"));
const GrantSignPage = lazy(() => import("./pages/GrantSignPage"));
const HazardHunt = lazy(() => import("./pages/HazardHunt"));
const Health360Page = lazy(() => import("./pages/Health360Page"));
const HealthcareOrganizingPage = lazy(() => import("./pages/HealthcareOrganizingPage"));
const IAMPage = lazy(() => import("./pages/IAMPage"));
const IBEW1288Page = lazy(() => import("./pages/IBEW1288Page"));
const IronWorkers167Page = lazy(() => import("./pages/IronWorkers167Page"));
const JailCampaignAdmin = lazy(() => import("./pages/JailCampaignAdmin"));
const JailCourse = lazy(() => import("./pages/JailCourse"));
const KnotTying = lazy(() => import("./pages/KnotTying"));
const LAFedPage = lazy(() => import("./pages/LAFedPage"));
const LCLAAPage = lazy(() => import("./pages/LCLAAPage"));
const LaborAndAIPage = lazy(() => import("./pages/LaborAndAIPage"));
const LaborHistory101ExamPage = lazy(() => import("./pages/LaborHistory101ExamPage"));
const LaborHistory101InstructorPage = lazy(() => import("./pages/LaborHistory101InstructorPage"));
const LaborHistory101MaterialViewer = lazy(() => import("./pages/LaborHistory101MaterialViewer"));
const LaborHistory101Page = lazy(() => import("./pages/LaborHistory101Page"));
const LaborPulpitAdmin = lazy(() => import("./pages/LaborPulpitAdmin"));
const LaborPulpitPage = lazy(() => import("./pages/LaborPulpitPage"));
const AngelaBurtonPage = lazy(() => import("./pages/AngelaBurtonPage"));
const LaborSouthPage = lazy(() => import("./pages/LaborSouthPage"));
const LaborStoriesAdmin = lazy(() => import("./pages/LaborStoriesAdmin"));
const LaborStoriesPage = lazy(() => import("./pages/LaborStoriesPage"));
const LadderSafety = lazy(() => import("./pages/LadderSafety"));
const LearnHub = lazy(() => import("./pages/LearnHub"));
const ExploreLaborEducation = lazy(() => import("./pages/ExploreLaborEducation"));
const LinkHealth = lazy(() => import("./pages/LinkHealth"));
const CLCEndorsementsPage = lazy(() => import("./pages/CLCEndorsementsPage"));
const DigitalMediaPage = lazy(() => import("./pages/DigitalMediaPage"));
const LockoutTagout = lazy(() => import("./pages/LockoutTagout"));
const MAAFLCIO = lazy(() => import("./pages/MAAFLCIO"));
const MNAFLCIO = lazy(() => import("./pages/MNAFLCIO"));
const NCAFLCIO = lazy(() => import("./pages/NCAFLCIO"));
const DrMcKinney = lazy(() => import("./pages/DrMcKinney"));
const TiffanyGray = lazy(() => import("./pages/TiffanyGray"));
const AdrianMillsap = lazy(() => import("./pages/AdrianMillsap"));
const GailTyree = lazy(() => import("./pages/GailTyree"));
const BennyOverton = lazy(() => import("./pages/BennyOverton"));
const RoseTurner = lazy(() => import("./pages/RoseTurner"));
const RoderickRichmond = lazy(() => import("./pages/RoderickRichmond"));
const SedrickTurnerPage = lazy(() => import("./pages/SedrickTurnerPage"));
const MSCSPage = lazy(() => import("./pages/MSCSPage"));
const MSCSHubPage = lazy(() => import("./pages/MSCSHubPage"));
const LaborRadioPage = lazy(() => import("./pages/LaborRadioPage"));
const Ballot2026Page = lazy(() => import("./pages/Ballot2026Page"));
const Ballot2026Handout = lazy(() => import("./pages/Ballot2026Handout"));
const Ballot2026PositionBrief = lazy(() => import("./pages/Ballot2026PositionBrief"));
const SafetyReport = lazy(() => import("./pages/SafetyReport"));
const SafetyTrack = lazy(() => import("./pages/SafetyTrack"));
const SafetyCommittee = lazy(() => import("./pages/SafetyCommittee"));
const SafetyPoster = lazy(() => import("./pages/SafetyPoster"));
const SafetyPosterPack = lazy(() => import("./pages/SafetyPosterPack"));
const SafetyShare = lazy(() => import("./pages/SafetyShare"));
const ApprenticeTalk = lazy(() => import("./pages/ApprenticeTalk"));
const ChemInventory = lazy(() => import("./pages/ChemInventory"));
const ChemInventorySheet = lazy(() => import("./pages/ChemInventorySheet"));
const SafetyRecord = lazy(() => import("./pages/SafetyRecord"));
const Amendments2026Page = lazy(() => import("./pages/Amendments2026Page"));
const Amendments2026Handout = lazy(() => import("./pages/Amendments2026Handout"));
const LegendsPage = lazy(() => import("./pages/LegendsPage"));
const MSAFLCIO = lazy(() => import("./pages/MSAFLCIO"));
const MusicAdmin = lazy(() => import("./pages/MusicAdmin"));
const MusicPage = lazy(() => import("./pages/MusicPage"));
const OPEIULocal277Page = lazy(() => import("./pages/OPEIULocal277Page"));
const SEIULocal205Page = lazy(() => import("./pages/SEIULocal205Page"));
const PPEDrill = lazy(() => import("./pages/PPEDrill"));
const PartnerOrgPage = lazy(() => import("./pages/PartnerOrgPage"));
const ParentPowerPage = lazy(() => import("./pages/ParentPowerPage"));
const CxffeeblackPage = lazy(() => import("./pages/CxffeeblackPage"));
const TNBallotsPage = lazy(() => import("./pages/TNBallotsPage"));
const ThorntonForTennessee = lazy(() => import("./pages/ThorntonForTennessee"));
const MalikGarrettPage = lazy(() => import("./pages/MalikGarrettPage"));
const CLCInterviewsPage = lazy(() => import("./pages/CLCInterviewsPage"));
const PillarPage = lazy(() => import("./pages/PillarPage"));
const PrideAtWorkPage = lazy(() => import("./pages/PrideAtWorkPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PrivateArchive = lazy(() => import("./pages/PrivateArchive"));
const SEIUPage = lazy(() => import("./pages/SEIUPage"));
const SimHub = lazy(() => import("./pages/SimHub"));
const PathwaysPage = lazy(() => import("./pages/SkilledTradesPathwaysPage"));
const StateVoterHub = lazy(() => import("./pages/StateVoterHub"));
const T4SAdmin = lazy(() => import("./pages/T4SAdmin"));
const T4SLeadership = lazy(() => import("./pages/T4SLeadership"));
const JerryReesePage = lazy(() => import("./pages/JerryReesePage"));
const SportsUnionsPage = lazy(() => import("./pages/SportsUnionsPage"));
const LaborLiaisonLetter = lazy(() => import("./pages/LaborLiaisonLetter"));
const TNEarlyVote2026 = lazy(() => import("./pages/TNEarlyVote2026"));
const TNFriendsOfLaborFlyer = lazy(() => import("./pages/TNFriendsOfLaborFlyer"));
const ShelbyLaborSlate = lazy(() => import("./pages/ShelbyLaborSlate"));
const ResultsNight = lazy(() => import("./pages/ResultsNight"));
const MSFriendsOfLaborFlyer = lazy(() => import("./pages/MSFriendsOfLaborFlyer"));
const TNAFLCIO = lazy(() => import("./pages/TNAFLCIO"));
const ARAFLCIO = lazy(() => import("./pages/ARAFLCIO"));
const Join1733Page = lazy(() => import("./pages/Join1733Page"));
const Join1733Admin = lazy(() => import("./pages/Join1733Admin"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const TheSouth = lazy(() => import("./pages/TheSouth"));
const TheSouthAdmin = lazy(() => import("./pages/TheSouthAdmin"));
const TheSouthPressKit = lazy(() => import("./pages/TheSouthPressKit"));
const ToolID = lazy(() => import("./pages/ToolID"));
const ToolTrainingAdmin = lazy(() => import("./pages/ToolTrainingAdmin"));
const ToolTrainingPage = lazy(() => import("./pages/ToolTrainingPage"));
const TownHallAdmin = lazy(() => import("./pages/TownHallAdmin"));
const TownHallPage = lazy(() => import("./pages/TownHallPage"));
const UAWPage = lazy(() => import("./pages/UAWPage"));
const UAW737Page = lazy(() => import("./pages/UAW737Page"));
const UAWLocalPage = lazy(() => import("./pages/UAWLocalPage"));
const UAWRegion8Page = lazy(() => import("./pages/UAWRegion8Page"));
const UAWRegion8Handout = lazy(() => import("./pages/UAWRegion8Handout"));
const UAWLocalAdmin = lazy(() => import("./pages/UAWLocalAdmin"));
const UFCWLocal1529Admin = lazy(() => import("./pages/UFCWLocal1529Admin"));
const PillarJoinAdmin = lazy(() => import("./pages/PillarJoinAdmin"));
const UnionIQPage = lazy(() => import("./pages/UnionIQPage"));
const UFCWPage = lazy(() => import("./pages/UFCWPage"));
const USSWPage = lazy(() => import("./pages/USSWPage"));
const USWPage = lazy(() => import("./pages/USWPage"));
const UnitedWayAdmin = lazy(() => import("./pages/UnitedWayAdmin"));
const UnitedWayPage = lazy(() => import("./pages/UnitedWayPage"));
const VondaFellowsAdminPage = lazy(() => import("./pages/VondaFellowsAdminPage"));
const VondaFellowsPage = lazy(() => import("./pages/VondaFellowsPage"));
const VondaMcDanielPage = lazy(() => import("./pages/VondaMcDanielPage"));
const VondaSchoolPage = lazy(() => import("./pages/VondaSchoolPage"));
const VondaVideoUploadPage = lazy(() => import("./pages/VondaVideoUploadPage"));
const VoterHub = lazy(() => import("./pages/VoterHub"));
const WSLCPage = lazy(() => import("./pages/WSLCPage"));
const WeRefuseToBeDivided = lazy(() => import("./pages/WeRefuseToBeDivided"));
const WomenEmpowermentPage = lazy(() => import("./pages/WomenEmpowermentPage"));
const WorkersInterfaithPage = lazy(() => import("./pages/WorkersInterfaithPage"));
const WINFundraisingCourse = lazy(() => import("./pages/WINFundraisingCourse"));
const WINWorkerRightsCourse = lazy(() => import("./pages/WINWorkerRightsCourse"));
const WINOrgGrowthCourse = lazy(() => import("./pages/WINOrgGrowthCourse"));
const WINFacilitatorKit = lazy(() => import("./pages/WINFacilitatorKit"));
const WorkingAmericaPage = lazy(() => import("./pages/WorkingAmericaPage"));
const WriteInBradshaw = lazy(() => import("./pages/WriteInBradshaw"));
const BackToSchoolPage = lazy(() => import("./pages/BackToSchoolPage"));
const StudentHub = lazy(() => import("./pages/StudentHub"));

// Lazy pages (named exports)
const AIChat = ln(() => import("./pages/AIChat"), "AIChat");
const AdultLibrary = ln(() => import("./pages/AdultLibrary"), "AdultLibrary");
const AnalyticsDashboard = ln(() => import("./pages/AnalyticsDashboard"), "AnalyticsDashboard");
const ArticleDetail = ln(() => import("./pages/ArticleDetail"), "ArticleDetail");
const BenefitsCalculator = ln(() => import("./pages/BenefitsCalculator"), "BenefitsCalculator");
const CLCDirectory = ln(() => import("./pages/CLCDirectory"), "CLCDirectory");
const CompareUnions = ln(() => import("./pages/CompareUnions"), "CompareUnions");
const D10Register = ln(() => import("./pages/D10Register"), "D10Register");
const District10Admin = ln(() => import("./pages/District10Admin"), "District10Admin");
const District10PlanView = lazy(() => import("./pages/District10PlanView"));
const PaymentFormsPage = lazy(() => import("./pages/PaymentFormsPage"));
const W9UploadPage = lazy(() => import("./pages/W9UploadPage"));
const W9AgentPage = lazy(() => import("./pages/W9AgentPage"));
const D10CurriculumPage = ln(() => import("./pages/D10CurriculumPage"), "D10CurriculumPage");
const D10StudentCurriculum = lazy(() => import("./pages/D10StudentCurriculum"));
const D10TeachCurriculum = lazy(() => import("./pages/D10TeachCurriculum"));
const D10Workbook = lazy(() => import("./pages/D10Workbook"));
const D10Flyer = lazy(() => import("./pages/D10Flyer"));
const D10SchoolSignup = lazy(() => import("./pages/D10SchoolSignup"));
const D10CounselorLetter = lazy(() => import("./pages/D10CounselorLetter"));
const ElamFlyer = lazy(() => import("./pages/ElamFlyer"));
const FlyerScan = lazy(() => import("./pages/FlyerScan"));
const SafeHavenPage = ln(() => import("./pages/SafeHavenPage"), "SafeHavenPage");
const StandStrongPage = ln(() => import("./pages/StandStrongPage"), "StandStrongPage");
const GiveawayPage = ln(() => import("./pages/GiveawayPage"), "GiveawayPage");
const InstructorPortal = ln(() => import("./pages/InstructorPortal"), "InstructorPortal");
const JobReadiness = ln(() => import("./pages/JobReadiness"), "JobReadiness");
const JobSearch = ln(() => import("./pages/JobSearch"), "JobSearch");
const KnowYourRights = ln(() => import("./pages/KnowYourRights"), "KnowYourRights");
const LIUNALocal386Page = ln(() => import("./pages/LIUNALocalsPage"), "LIUNALocal386Page");
const LIUNALocal818Page = ln(() => import("./pages/LIUNALocalsPage"), "LIUNALocal818Page");
const LIUNALocal846Page = ln(() => import("./pages/LIUNALocalsPage"), "LIUNALocal846Page");
const LegislativeTracker = ln(() => import("./pages/LegislativeTracker"), "LegislativeTracker");
const Local1733Page = ln(() => import("./pages/Local1733Page"), "Local1733Page");
const Local252GAdmin = ln(() => import("./pages/Local252GAdmin"), "Local252GAdmin");
const Local252GPage = ln(() => import("./pages/Local252GPage"), "Local252GPage");
const Grievance252GPage = ln(() => import("./pages/Grievance252GPage"), "Grievance252GPage");
const Local3806Page = ln(() => import("./pages/Local3806Page"), "Local3806Page");
const Local3866Admin = ln(() => import("./pages/Local3866Admin"), "Local3866Admin");
const Local3866Page = ln(() => import("./pages/Local3866Page"), "Local3866Page");
const Local3GAdmin = ln(() => import("./pages/Local3GAdmin"), "Local3GAdmin");
const Local3GPage = ln(() => import("./pages/Local3GPage"), "Local3GPage");
const Local667Page = ln(() => import("./pages/Local667Page"), "Local667Page");
const Local84Admin = ln(() => import("./pages/Local84Admin"), "Local84Admin");
const Local84Portal = ln(() => import("./pages/Local84Portal"), "Local84Portal");
const MemphisForAllPage = ln(() => import("./pages/MemphisForAllPage"), "MemphisForAllPage");
const OpEdMLGWPage = lazy(() => import("./pages/OpEdMLGWPage"));
const StevieMooreStoryPage = lazy(() => import("./pages/StevieMooreStoryPage"));
const SarahCarpenterStoryPage = lazy(() => import("./pages/SarahCarpenterStoryPage"));
const RuthBanksStoryPage = lazy(() => import("./pages/RuthBanksStoryPage"));
const PainSavedMyLifeStoryPage = lazy(() => import("./pages/PainSavedMyLifeStoryPage"));
const KontjiAnthonyStoryPage = lazy(() => import("./pages/KontjiAnthonyStoryPage"));
const DavidPorterStoryPage = lazy(() => import("./pages/DavidPorterStoryPage"));
const LegalPartnersPage = lazy(() => import("./pages/LegalPartnersPage"));
const FeedTheNeedyPage = lazy(() => import("./pages/FeedTheNeedyPage"));
const DrivingTheDreamPage = lazy(() => import("./pages/DrivingTheDreamPage"));
const CaregiversPage = lazy(() => import("./pages/CaregiversPage"));
const MemphisPolicePage = ln(() => import("./pages/MemphisPolicePage"), "MemphisPolicePage");
const NotificationsAdmin = ln(() => import("./pages/NotificationsAdmin"), "NotificationsAdmin");
const OrganizingToolkit = ln(() => import("./pages/OrganizingToolkit"), "OrganizingToolkit");
const ProgressTracker = ln(() => import("./pages/ProgressTracker"), "ProgressTracker");
const QRTools = ln(() => import("./pages/QRTools"), "QRTools");
const FileDropPage = lazy(() => import("./pages/FileDropPage"));
const GrantTrackerPage = lazy(() => import("./pages/GrantTrackerPage"));
const SharePage = ln(() => import("./pages/SharePage"), "SharePage");
const ShelbyCountyDSAPage = ln(() => import("./pages/ShelbyCountyDSAPage"), "ShelbyCountyDSAPage");
const SponsorshipPage = ln(() => import("./pages/SponsorshipPage"), "SponsorshipPage");
const StateAFLCIODirectory = ln(() => import("./pages/StateAFLCIODirectory"), "StateAFLCIODirectory");
const StewardLeaderboard = ln(() => import("./pages/StewardLeaderboard"), "StewardLeaderboard");
const NationalTrainingDirectory = ln(() => import("./pages/StewardTraining"), "NationalTrainingDirectory");
const StewardCertificatePage = ln(() => import("./pages/StewardTraining"), "StewardCertificatePage");
const StewardModuleDetail = ln(() => import("./pages/StewardTraining"), "StewardModuleDetail");
const StewardTrainingHub = ln(() => import("./pages/StewardTraining"), "StewardTrainingHub");
const SurveysAdmin = ln(() => import("./pages/SurveysAdmin"), "SurveysAdmin");
const TCULodge794Page = ln(() => import("./pages/TCULodge794Page"), "TCULodge794Page");
const UFCWLocal1529Page = ln(() => import("./pages/UFCWLocal1529Page"), "UFCWLocal1529Page");
const IBEW474Page = ln(() => import("./pages/IBEW474Page"), "IBEW474Page");
const GLCLCPage = ln(() => import("./pages/GLCLCPage"), "GLCLCPage");
const KentuckyAFLCIOPage = ln(() => import("./pages/KentuckyAFLCIOPage"), "KentuckyAFLCIOPage");
const USWLocal7655Admin = ln(() => import("./pages/USWLocal7655Admin"), "USWLocal7655Admin");
const USWLocal7655Page = ln(() => import("./pages/USWLocal7655Page"), "USWLocal7655Page");
const UnionDetail = ln(() => import("./pages/UnionDetail"), "UnionDetail");
const SponsorsThankYouPage = lazy(() => import("./pages/SponsorsThankYouPage"));
const PrintFilesPage = lazy(() => import("./pages/PrintFilesPage"));
const OneShelbyDocsPage = lazy(() => import("./pages/OneShelbyDocsPage"));
const BradshawSolutionsPage = lazy(() => import("./pages/BradshawSolutionsPage"));
const IPTVPage = lazy(() => import("./pages/IPTVPage"));
const NAALCPage = lazy(() => import("./pages/NAALCPage"));
const UnionDirectory = ln(() => import("./pages/UnionDirectory"), "UnionDirectory");
const UnionJobBoard = ln(() => import("./pages/UnionJobBoard"), "UnionJobBoard");
const VotePage = ln(() => import("./pages/VotePage"), "VotePage");
const YouthLibrary = ln(() => import("./pages/YouthLibrary"), "YouthLibrary");

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="page-loader">
    <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
  </div>
);

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL hash - handle auth callback
  // Check both location.hash and window.location.hash for better compatibility
  const hash = location.hash || window.location.hash;
  if (hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <ScrollToTop />
    <JuneteenthBanner />
    <MobileBottomNav />
    <PWAInstallPrompt />
    <div className="md:pb-0 pb-14">
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<><Navbar /><FeaturedEventsBanner /><AICourseBanner /><ConventionBanner /><LandingPage /><div className="container mx-auto px-4 my-8 space-y-6"><DailyStreakCard /><GrowUnionsCard /><JailCampaignCard /></div><PartnersBar /><Footer /></>} />
      <Route path="/unions" element={<><Navbar /><UnionDirectory /><Footer /></>} />
      <Route path="/unions/:union_id" element={<><Navbar /><UnionDetail /><Footer /></>} />
      <Route path="/sponsors" element={<><Navbar /><SponsorsThankYouPage /><Footer /></>} />
      <Route path="/print-files" element={<><Navbar /><PrintFilesPage /><Footer /></>} />
      <Route path="/one-shelby" element={<OneShelbyDocsPage />} />
      <Route path="/compare" element={<><Navbar /><CompareUnions /><Footer /></>} />
      <Route path="/learn" element={<><Navbar /><LearnHub /><Footer /></>} />
      <Route path="/explore" element={<><Navbar /><ExploreLaborEducation /><Footer /></>} />
      <Route path="/sim" element={<><Navbar /><SimHub /><Footer /></>} />
      <Route path="/career-pathways" element={<><Navbar /><CareerPathwaysPage /><Footer /></>} />
      <Route path="/career-pathways-admin" element={<><Navbar /><CareerPathwaysAdmin /><Footer /></>} />
      <Route path="/sim/ppe" element={<><Navbar /><div className="container mx-auto px-4 py-8"><PPEDrill /></div><Footer /></>} />
      <Route path="/sim/hazard-hunt" element={<><Navbar /><div className="container mx-auto px-4 py-8"><HazardHunt /></div><Footer /></>} />
      <Route path="/sim/tool-id" element={<><Navbar /><div className="container mx-auto px-4 py-8"><ToolID /></div><Footer /></>} />
      <Route path="/sim/ladder-safety" element={<><Navbar /><div className="container mx-auto px-4 py-8"><LadderSafety /></div><Footer /></>} />
      <Route path="/sim/lockout-tagout" element={<><Navbar /><div className="container mx-auto px-4 py-8"><LockoutTagout /></div><Footer /></>} />
      <Route path="/sim/knot-tying" element={<><Navbar /><div className="container mx-auto px-4 py-8"><KnotTying /></div><Footer /></>} />
      <Route path="/sim/blueprint" element={<><Navbar /><div className="container mx-auto px-4 py-8"><BlueprintReading /></div><Footer /></>} />
      <Route path="/t4s" element={<><Navbar /><div className="container mx-auto px-4 py-8"><T4SLeadership /></div><Footer /></>} />
      <Route path="/jerry-reese" element={<><Navbar /><JerryReesePage /><Footer /></>} />
      <Route path="/sports-unions" element={<><Navbar /><SportsUnionsPage /><Footer /></>} />
      <Route path="/labor-liaison-letter" element={<LaborLiaisonLetter />} />
      <Route path="/t4s-admin" element={<><Navbar /><div className="container mx-auto px-4 py-8"><T4SAdmin /></div><Footer /></>} />
      <Route path="/learn/:article_id" element={<><Navbar /><ArticleDetail /><Footer /></>} />
      <Route path="/certificate" element={<><Navbar /><CertificatePage /><Footer /></>} />
      <Route path="/steward-training" element={<><Navbar /><StewardTrainingHub /><Footer /></>} />
      <Route path="/steward-training/module/:module_id" element={<><Navbar /><StewardModuleDetail /><Footer /></>} />
      <Route path="/steward-training/certificate" element={<><Navbar /><StewardCertificatePage /><Footer /></>} />
      <Route path="/steward-training/directory" element={<><Navbar /><NationalTrainingDirectory /><Footer /></>} />
      <Route path="/steward-training/leaderboard" element={<><Navbar /><StewardLeaderboard /><Footer /></>} />
      <Route path="/terms" element={<><Navbar /><TermsOfService /><Footer /></>} />
      <Route path="/privacy" element={<><Navbar /><PrivacyPolicy /><Footer /></>} />
      <Route path="/delete-my-data" element={<><Navbar /><DeleteMyData /><Footer /></>} />
      <Route path="/strikes" element={<><Navbar /><StrikeTracker /><Footer /></>} />
      <Route path="/media" element={<><Navbar /><MediaPage /><Footer /></>} />
      <Route path="/scholarships" element={<><Navbar /><ScholarshipsPage /><Footer /></>} />
      <Route path="/print-shop" element={<><Navbar /><PrintShopPage /><Footer /></>} />
      <Route path="/book" element={<><Navbar /><BookPage /><Footer /></>} />
      <Route path="/book/admin" element={<BookAdmin />} />
      <Route path="/gallery" element={<><Navbar /><GalleryPage /><Footer /></>} />
      <Route path="/jobs" element={<><Navbar /><UnionJobBoard /><Footer /></>} />
      <Route path="/learn/benefits-calculator" element={<><Navbar /><BenefitsCalculator /><Footer /></>} />
      <Route path="/learn/know-your-rights" element={<><Navbar /><KnowYourRights /><Footer /></>} />
      <Route path="/learn/organizing-toolkit" element={<><Navbar /><OrganizingToolkit /><Footer /></>} />
      <Route path="/learn/legislative-tracker" element={<><Navbar /><LegislativeTracker /><Footer /></>} />
      <Route path="/memphis-police" element={<><Navbar /><MemphisPolicePage /><Footer /></>} />
      <Route path="/shelby-county-dsa" element={<><Navbar /><ShelbyCountyDSAPage /><Footer /></>} />
      <Route path="/building-a-better-jail" element={<><Navbar /><BuildingBetterJailPage /><Footer /></>} />
      <Route path="/history-at-the-table" element={<><Navbar /><HistoryAtTheTablePage /><Footer /></>} />
      <Route path="/jail-course" element={<><Navbar /><JailCourse /><Footer /></>} />
      <Route path="/jail-campaign-admin" element={<JailCampaignAdmin />} />
      <Route path="/tcu-794" element={<><Navbar /><TCULodge794Page /><Footer /></>} />
      <Route path="/local-1733" element={<><Navbar /><Local1733Page /><Footer /></>} />
      <Route path="/local-252g" element={<><Navbar /><Local252GPage /><Footer /></>} />
      <Route path="/252g-grievance" element={<><Navbar /><Grievance252GPage /><Footer /></>} />
      <Route path="/local-3g" element={<><Navbar /><Local3GPage /><Footer /></>} />
      <Route path="/local3g" element={<><Navbar /><Local3GPage /><Footer /></>} />
      <Route path="/local-3806" element={<><Navbar /><Local3806Page /><Footer /></>} />
      <Route path="/local-3866" element={<><Navbar /><Local3866Page /><Footer /></>} />
      <Route path="/local-667" element={<><Navbar /><Local667Page /><Footer /></>} />
      <Route path="/ufcw-1529" element={<><Navbar /><UFCWLocal1529Page /><Footer /></>} />
      <Route path="/ufcw-1529/admin" element={<UFCWLocal1529Admin />} />
      <Route path="/ibew-474" element={<><Navbar /><IBEW474Page /><Footer /></>} />
      <Route path="/tool-training" element={<><Navbar /><ToolTrainingPage /><Footer /></>} />
      <Route path="/tool-training/admin" element={<ToolTrainingAdmin />} />
      <Route path="/labor-history-101" element={<><Navbar /><LaborHistory101Page /><Footer /></>} />
      <Route path="/labor-history-101/exam" element={<><Navbar /><LaborHistory101ExamPage /><Footer /></>} />
      <Route path="/labor-history-101/material/:slug" element={<LaborHistory101MaterialViewer />} />
      <Route path="/labor-history-101/instructor" element={<LaborHistory101InstructorPage />} />
      <Route path="/usw-7655" element={<><Navbar /><USWLocal7655Page /><Footer /></>} />
      <Route path="/usw-7655-admin" element={<USWLocal7655Admin />} />
      <Route path="/liuna-386" element={<><Navbar /><LIUNALocal386Page /><Footer /></>} />
      <Route path="/liuna-818" element={<><Navbar /><LIUNALocal818Page /><Footer /></>} />
      <Route path="/liuna-846" element={<><Navbar /><LIUNALocal846Page /><Footer /></>} />
      <Route path="/memphis-for-all" element={<><Navbar /><MemphisForAllPage /><Footer /></>} />
      <Route path="/oped-mlgw" element={<><Navbar /><OpEdMLGWPage /><Footer /></>} />
      <Route path="/stevie-moore-story" element={<><Navbar /><StevieMooreStoryPage /><Footer /></>} />
      <Route path="/sarah-carpenter-story" element={<><Navbar /><SarahCarpenterStoryPage /><Footer /></>} />
      <Route path="/ruth-banks-story" element={<><Navbar /><RuthBanksStoryPage /><Footer /></>} />
      <Route path="/pain-saved-my-life" element={<><Navbar /><PainSavedMyLifeStoryPage /><Footer /></>} />
      <Route path="/kontji-anthony" element={<><Navbar /><KontjiAnthonyStoryPage /><Footer /></>} />
      <Route path="/david-porter" element={<><Navbar /><DavidPorterStoryPage /><Footer /></>} />
      <Route path="/caregivers" element={<><Navbar /><CaregiversPage /><Footer /></>} />
      <Route path="/youth-library" element={<><Navbar /><YouthLibrary /><Footer /></>} />
      <Route path="/back-to-school" element={<><Navbar /><BackToSchoolPage /><Footer /></>} />
      <Route path="/student-hub" element={<><Navbar /><StudentHub /><Footer /></>} />
      <Route path="/district10-admin" element={<><Navbar /><District10Admin /><Footer /></>} />
      <Route path="/district10-plan/:token" element={<District10PlanView />} />
      <Route path="/d10-forms/:token" element={<PaymentFormsPage />} />
      <Route path="/d10-w9/:token" element={<W9UploadPage />} />
      <Route path="/d10-w9-agent/:token" element={<W9AgentPage />} />
      <Route path="/d10-curriculum" element={<><Navbar /><D10CurriculumPage /><Footer /></>} />
      <Route path="/d10-class" element={<><Navbar /><D10StudentCurriculum /><Footer /></>} />
      <Route path="/d10-teach/:token" element={<D10TeachCurriculum />} />
      <Route path="/d10-workbook" element={<D10Workbook />} />
      <Route path="/d10-flyer" element={<D10Flyer />} />
      <Route path="/d10-school-signup" element={<D10SchoolSignup />} />
      <Route path="/d10-counselor-letter" element={<D10CounselorLetter />} />
      <Route path="/e/:code" element={<FlyerScan />} />
      <Route path="/safe-haven" element={<><Navbar /><SafeHavenPage /><Footer /></>} />
      <Route path="/stand-strong" element={<><Navbar /><StandStrongPage /><Footer /></>} />
      <Route path="/register" element={<><Navbar /><D10Register /><Footer /></>} />
      <Route path="/instructor" element={<><Navbar /><InstructorPortal /><Footer /></>} />
      <Route path="/job-readiness" element={<><Navbar /><JobReadiness /><Footer /></>} />
      <Route path="/labor-library" element={<><Navbar /><AdultLibrary /><Footer /></>} />
      <Route path="/job-search" element={<><Navbar /><JobSearch /><Footer /></>} />
      <Route path="/sponsor" element={<><Navbar /><SponsorshipPage /><Footer /></>} />
      <Route path="/progress" element={<><Navbar /><ProgressTracker /><Footer /></>} />
      <Route path="/qr-tools" element={<><Navbar /><QRTools /><Footer /></>} />
      <Route path="/file-drop" element={<FileDropPage />} />
      <Route path="/grant-tracker" element={<GrantTrackerPage />} />
      <Route path="/private-archive" element={<PrivateArchive />} />
      <Route path="/grant-admin" element={<GrantSignAdmin />} />
      <Route path="/grant-sign/:token" element={<GrantSignPage />} />
      <Route path="/music" element={<><Navbar /><MusicPage /><Footer /></>} />
      <Route path="/music-admin" element={<MusicAdmin />} />
      <Route path="/state-aflcio" element={<><Navbar /><StateAFLCIODirectory /><Footer /></>} />
      <Route path="/ms-aflcio" element={<><Navbar /><MSAFLCIO /><Footer /></>} />
      <Route path="/tn-aflcio" element={<><Navbar /><TNAFLCIO /><Footer /></>} />
      <Route path="/ar-aflcio" element={<><Navbar /><ARAFLCIO /><Footer /></>} />
      <Route path="/join-1733" element={<><Navbar /><Join1733Page /><Footer /></>} />
      <Route path="/join-1733/admin" element={<Join1733Admin />} />
      <Route path="/the-south" element={<><Navbar /><TheSouth /><Footer /></>} />
      <Route path="/the-south/press-kit" element={<><Navbar /><TheSouthPressKit /><Footer /></>} />
      <Route path="/the-south-admin" element={<><Navbar /><TheSouthAdmin /><Footer /></>} />
      <Route path="/voter-hub" element={<><Navbar /><VoterHub /><Footer /></>} />
      <Route path="/tn-early-vote" element={<><Navbar /><TNEarlyVote2026 /><Footer /></>} />
      <Route path="/ms-friends-of-labor-flyer" element={<MSFriendsOfLaborFlyer />} />
      <Route path="/tn-friends-of-labor-flyer" element={<TNFriendsOfLaborFlyer />} />
      <Route path="/shelby-labor-slate" element={<ShelbyLaborSlate />} />
      <Route path="/results-night" element={<ResultsNight />} />
      <Route path="/voter-hub/:stateCode" element={<><Navbar /><StateVoterHub /><Footer /></>} />
      <Route path="/civic-power-playbook" element={<><Navbar /><CivicPowerPlaybookRedirect /><Footer /></>} />
      <Route path="/playbook" element={<><Navbar /><CivicPowerPlaybookRedirect /><Footer /></>} />
      <Route path="/write-in-bradshaw" element={<><Navbar /><WriteInBradshaw /><Footer /></>} />
      <Route path="/opeiu-277" element={<><Navbar /><OPEIULocal277Page /><Footer /></>} />
      <Route path="/seiu-205" element={<><Navbar /><SEIULocal205Page /><Footer /></>} />
      <Route path="/four-pillars" element={<><Navbar /><FourPillars /><Footer /></>} />
      <Route path="/four-pillars/signups" element={<PillarJoinAdmin />} />
      <Route path="/union-iq" element={<UnionIQPage />} />
      <Route path="/glclc" element={<><Navbar /><GLCLCPage /><Footer /></>} />
      <Route path="/ky-aflcio" element={<><Navbar /><KentuckyAFLCIOPage /><Footer /></>} />
      <Route path="/dr-lawson" element={<><Navbar /><DrLawson /><Footer /></>} />
      <Route path="/pillars/:slug" element={<><Navbar /><PillarPage /><Footer /></>} />
      <Route path="/link-health" element={<LinkHealth />} />
      <Route path="/mn-aflcio" element={<><Navbar /><ConventionBanner /><MNAFLCIO /><Footer /></>} />
      <Route path="/min-aflcio" element={<><Navbar /><ConventionBanner /><MNAFLCIO /><Footer /></>} />
      <Route path="/ma-aflcio" element={<><Navbar /><ConventionBanner /><MAAFLCIO /><Footer /></>} />
      <Route path="/nc-aflcio" element={<><Navbar /><NCAFLCIO /><Footer /></>} />
      <Route path="/dr-mckinney" element={<><Navbar /><DrMcKinney /><Footer /></>} />
      <Route path="/tiffany-gray" element={<><Navbar /><TiffanyGray /><Footer /></>} />
      <Route path="/adrian-millsap" element={<><Navbar /><AdrianMillsap /><Footer /></>} />
      <Route path="/apprentice-talk" element={<><Navbar /><ApprenticeTalk /><Footer /></>} />
      <Route path="/gail-tyree" element={<><Navbar /><GailTyree /><Footer /></>} />
      <Route path="/benny-overton" element={<><Navbar /><BennyOverton /><Footer /></>} />
      <Route path="/rose-turner" element={<><Navbar /><RoseTurner /><Footer /></>} />
      <Route path="/roderick-richmond" element={<Navigate to="/mscs#superintendent" replace />} />
      <Route path="/sedrick-turner" element={<Navigate to="/mscs#partner" replace />} />
      <Route path="/mscs" element={<><Navbar /><MSCSHubPage /><Footer /></>} />
      <Route path="/labor-radio" element={<><Navbar /><LaborRadioPage /><Footer /></>} />
      <Route path="/ballot-2026" element={<><Navbar /><Ballot2026Page /><Footer /></>} />
      <Route path="/ballot-2026/handout" element={<Ballot2026Handout />} />
      <Route path="/ballot-2026/why" element={<Ballot2026PositionBrief />} />
      <Route path="/safety" element={<SafetyReport />} />
      <Route path="/safety/track" element={<SafetyTrack />} />
      <Route path="/safety/committee" element={<SafetyCommittee />} />
      <Route path="/safety/poster" element={<SafetyPoster />} />
      <Route path="/safety/posters" element={<SafetyPosterPack />} />
      <Route path="/safety/share" element={<SafetyShare />} />
      <Route path="/chem-inventory" element={<ChemInventory />} />
      <Route path="/chem-inventory/sheet/:id" element={<ChemInventorySheet />} />
      <Route path="/safety/record/:reportNo" element={<SafetyRecord />} />
      <Route path="/amendments-2026" element={<><Navbar /><Amendments2026Page /><Footer /></>} />
      <Route path="/amendments-2026/handout" element={<Amendments2026Handout />} />
      <Route path="/legends" element={<><Navbar /><LegendsPage /><Footer /></>} />
      <Route path="/mass-aflcio" element={<><Navbar /><ConventionBanner /><MAAFLCIO /><Footer /></>} />
      <Route path="/usw" element={<><Navbar /><ConventionBanner /><USWPage /><Footer /></>} />
      <Route path="/steelworkers" element={<><Navbar /><ConventionBanner /><USWPage /><Footer /></>} />
      <Route path="/afge" element={<><Navbar /><ConventionBanner /><AFGEPage /><Footer /></>} />
      <Route path="/government-employees" element={<><Navbar /><ConventionBanner /><AFGEPage /><Footer /></>} />
      <Route path="/ibew-1288" element={<><Navbar /><ConventionBanner /><IBEW1288Page /><Footer /></>} />
      <Route path="/unions/union_ibew_local_1288" element={<><Navbar /><ConventionBanner /><IBEW1288Page /><Footer /></>} />
      <Route path="/iron-workers-167" element={<><Navbar /><IronWorkers167Page /><Footer /></>} />
      <Route path="/ironworkers-167" element={<><Navbar /><IronWorkers167Page /><Footer /></>} />
      <Route path="/unions/union_ironworkers_local_167" element={<><Navbar /><IronWorkers167Page /><Footer /></>} />
      <Route path="/preview/credibility-bar" element={<CredibilityBarPreview />} />
      <Route path="/la-fed" element={<><Navbar /><ConventionBanner /><LAFedPage /><Footer /></>} />
      <Route path="/lafed" element={<><Navbar /><ConventionBanner /><LAFedPage /><Footer /></>} />
      <Route path="/iam" element={<><Navbar /><ConventionBanner /><IAMPage /><Footer /></>} />
      <Route path="/machinists" element={<><Navbar /><ConventionBanner /><IAMPage /><Footer /></>} />
      <Route path="/afscme" element={<><Navbar /><ConventionBanner /><AFSCMEPage /><Footer /></>} />
      <Route path="/uaw" element={<><Navbar /><ConventionBanner /><UAWPage /><Footer /></>} />
      <Route path="/uaw-737" element={<><Navbar /><ConventionBanner /><UAW737Page /><Footer /></>} />
      <Route path="/uaw-region-8" element={<><Navbar /><ConventionBanner /><UAWRegion8Page /><Footer /></>} />
      <Route path="/uaw-region-8/handout" element={<UAWRegion8Handout />} />
      <Route path="/uaw-local/:localId" element={<><Navbar /><ConventionBanner /><UAWLocalPage /><Footer /></>} />
      <Route path="/uaw-local/:localId/admin" element={<><Navbar /><UAWLocalAdmin /><Footer /></>} />
      <Route path="/autoworkers" element={<><Navbar /><ConventionBanner /><UAWPage /><Footer /></>} />
      <Route path="/cluw" element={<><Navbar /><ConventionBanner /><CLUWPage /><Footer /></>} />
      <Route path="/working-america" element={<><Navbar /><ConventionBanner /><WorkingAmericaPage /><Footer /></>} />
      <Route path="/workingamerica" element={<><Navbar /><ConventionBanner /><WorkingAmericaPage /><Footer /></>} />
      <Route path="/apala" element={<><Navbar /><ConventionBanner /><APALAPage /><Footer /></>} />
      <Route path="/aanhpi-labor" element={<><Navbar /><ConventionBanner /><APALAPage /><Footer /></>} />
      <Route path="/cbtu" element={<><Navbar /><ConventionBanner /><CBTUPage /><Footer /></>} />
      <Route path="/black-trade-unionists" element={<><Navbar /><ConventionBanner /><CBTUPage /><Footer /></>} />
      <Route path="/lclaa" element={<><Navbar /><ConventionBanner /><LCLAAPage /><Footer /></>} />
      <Route path="/latino-labor" element={<><Navbar /><ConventionBanner /><LCLAAPage /><Footer /></>} />
      <Route path="/apri" element={<><Navbar /><ConventionBanner /><APRIPage /><Footer /></>} />
      <Route path="/randolph-institute" element={<><Navbar /><ConventionBanner /><APRIPage /><Footer /></>} />
      <Route path="/pride-at-work" element={<><Navbar /><ConventionBanner /><PrideAtWorkPage /><Footer /></>} />
      <Route path="/prideatwork" element={<><Navbar /><ConventionBanner /><PrideAtWorkPage /><Footer /></>} />
      <Route path="/ufcw" element={<><Navbar /><ConventionBanner /><UFCWPage /><Footer /></>} />
      <Route path="/ufcw-international" element={<><Navbar /><ConventionBanner /><UFCWPage /><Footer /></>} />
      <Route path="/united-food-and-commercial-workers" element={<><Navbar /><ConventionBanner /><UFCWPage /><Footer /></>} />
      <Route path="/fair-price" element={<><Navbar /><FairPriceWatch /><Footer /></>} />
      <Route path="/predatory-pricing" element={<><Navbar /><FairPriceWatch /><Footer /></>} />
      <Route path="/fair-price-watch" element={<><Navbar /><FairPriceWatch /><Footer /></>} />
      <Route path="/fair-price-admin" element={<FairPriceAdmin />} />
      <Route path="/end-grocery-tax" element={<><Navbar /><EndGroceryTaxPage /><Footer /></>} />
      <Route path="/data-centers" element={<><Navbar /><DataCentersPage /><Footer /></>} />
      <Route path="/data-center-flyer" element={<><Navbar /><ClcDataCenterFlyerPage /><Footer /></>} />
      <Route path="/workforce-brief" element={<><Navbar /><WorkforceLaborBriefPage /><Footer /></>} />
      <Route path="/workforce-case/:token" element={<WorkforceCasePage />} />
      <Route path="/business-hub" element={<><Navbar /><BusinessHubPage /><Footer /></>} />
      <Route path="/cxffeeblack" element={<><Navbar /><CxffeeblackPage /><Footer /></>} />
      <Route path="/lincoln-alumni" element={<><Navbar /><LincolnAlumniPage /><Footer /></>} />
      <Route path="/contact" element={<><Navbar /><ContactPage /><Footer /></>} />
      <Route path="/negotiation-school" element={<><Navbar /><NegotiationSchool /><Footer /></>} />
      <Route path="/arbitration-school" element={<><Navbar /><ArbitrationSchool /><Footer /></>} />
      <Route path="/grievance-writing-school" element={<><Navbar /><GrievanceWritingSchool /><Footer /></>} />
      <Route path="/organizing-school" element={<><Navbar /><OrganizingSchool /><Footer /></>} />
      <Route path="/ai-workers-school" element={<><Navbar /><AIWorkersSchool /><Footer /></>} />
      <Route path="/academy" element={<><Navbar /><LaborEducationAcademy /><Footer /></>} />
      <Route path="/labor-education-academy" element={<><Navbar /><LaborEducationAcademy /><Footer /></>} />
      <Route path="/wslc" element={<><Navbar /><ConventionBanner /><WSLCPage /><Footer /></>} />
      <Route path="/wa-aflcio" element={<><Navbar /><ConventionBanner /><WSLCPage /><Footer /></>} />
      <Route path="/ussw" element={<><Navbar /><ConventionBanner /><USSWPage /><Footer /></>} />
      <Route path="/southern-service-workers" element={<><Navbar /><ConventionBanner /><USSWPage /><Footer /></>} />
      <Route path="/seiu" element={<><Navbar /><ConventionBanner /><SEIUPage /><Footer /></>} />
      <Route path="/labor-and-the-pulpit" element={<><Navbar /><LaborPulpitPage /><Footer /></>} />
      <Route path="/we-refuse-to-be-divided" element={<><Navbar /><WeRefuseToBeDivided /><Footer /></>} />
      <Route path="/wellness" element={<><Navbar /><Health360Page /><Footer /></>} />
      <Route path="/friends-of-labor" element={<><Navbar /><FriendsOfLaborPage /><Footer /></>} />
      <Route path="/friends-of-labor/ms" element={<><Navbar /><MSFriendsOfLaborPage /><Footer /></>} />
      <Route path="/clc-endorsements" element={<><Navbar /><CLCEndorsementsPage /><Footer /></>} />
      <Route path="/digital-media" element={<><Navbar /><DigitalMediaPage /><Footer /></>} />
      <Route path="/malik-garrett" element={<><Navbar /><MalikGarrettPage /><Footer /></>} />
      <Route path="/malik" element={<MalikGarrettPage standalone />} />
      <Route path="/clc-interviews" element={<><Navbar /><CLCInterviewsPage /><Footer /></>} />
      <Route path="/women-empowerment" element={<><Navbar /><WomenEmpowermentPage /><Footer /></>} />
      <Route path="/friends-of-labor/admin" element={<FriendsOfLaborAdmin />} />
      <Route path="/back-to-work" element={<><Navbar /><BackToWorkPage /><Footer /></>} />
      <Route path="/healthcare-organizing" element={<HealthcareOrganizingPage />} />
      <Route path="/town-hall" element={<TownHallPage />} />
      <Route path="/town-hall/admin" element={<TownHallAdmin />} />
      <Route path="/labor-stories" element={<><Navbar /><LaborStoriesPage /><Footer /></>} />
      <Route path="/labor-stories/admin" element={<LaborStoriesAdmin />} />
      <Route path="/united-way" element={<><Navbar /><UnitedWayPage /><Footer /></>} />
      <Route path="/united-way/admin" element={<UnitedWayAdmin />} />
      <Route path="/i-am-a-man" element={<><Navbar /><CivilRightsMuseumPage /><Footer /></>} />
      <Route path="/labor-south" element={<><Navbar /><LaborSouthPage /><Footer /></>} />
      <Route path="/community-unity-council" element={<><Navbar /><CommunityUnityCouncilPage /><Footer /></>} />
      <Route path="/labor-at-the-table" element={<><Navbar /><PartnerOrgPage slug="labor-at-the-table" /><Footer /></>} />
      <Route path="/jobs-with-justice" element={<><Navbar /><PartnerOrgPage slug="jobs-with-justice" /><Footer /></>} />
      <Route path="/jobs-with-justice-east-tn" element={<><Navbar /><PartnerOrgPage slug="jobs-with-justice-east-tn" /><Footer /></>} />
      <Route path="/national-jobs-for-all" element={<><Navbar /><PartnerOrgPage slug="national-jobs-for-all" /><Footer /></>} />
      <Route path="/protect-our-aquifer" element={<><Navbar /><PartnerOrgPage slug="protect-our-aquifer" /><Footer /></>} />
      <Route path="/tennessee-for-all" element={<><Navbar /><PartnerOrgPage slug="tennessee-for-all" /><Footer /></>} />
      <Route path="/progeny-place" element={<><Navbar /><PartnerOrgPage slug="progeny-place" /><Footer /></>} />
      <Route path="/gmlb-law" element={<><Navbar /><PartnerOrgPage slug="gmlb-law" /><Footer /></>} />
      <Route path="/union-auto-network" element={<><Navbar /><PartnerOrgPage slug="union-auto-network" /><Footer /></>} />
      <Route path="/sec4cd" element={<><Navbar /><PartnerOrgPage slug="sec4cd" /><Footer /></>} />
      <Route path="/working-families-party" element={<><Navbar /><PartnerOrgPage slug="working-families-party" /><Footer /></>} />
      <Route path="/noah" element={<><Navbar /><PartnerOrgPage slug="noah" /><Footer /></>} />
      <Route path="/dart" element={<><Navbar /><PartnerOrgPage slug="dart" /><Footer /></>} />
      <Route path="/mecca-memphis" element={<><Navbar /><PartnerOrgPage slug="mecca-memphis" /><Footer /></>} />
      <Route path="/youdle" element={<><Navbar /><PartnerOrgPage slug="youdle" /><Footer /></>} />
      <Route path="/harris-homeownership" element={<><Navbar /><PartnerOrgPage slug="harris-homeownership" /><Footer /></>} />
      <Route path="/xcelente-trucking" element={<><Navbar /><PartnerOrgPage slug="xcelente-trucking" /><Footer /></>} />
      <Route path="/we-fight-monsters" element={<><Navbar /><PartnerOrgPage slug="we-fight-monsters" /><Footer /></>} />
      <Route path="/bright-fame" element={<><Navbar /><PartnerOrgPage slug="bright-fame" /><Footer /></>} />
      <Route path="/memphis-allies" element={<><Navbar /><PartnerOrgPage slug="memphis-allies" /><Footer /></>} />
      <Route path="/second-chance-hiring" element={<><Navbar /><SecondChanceHiringPage /><Footer /></>} />
      <Route path="/highlander" element={<><Navbar /><PartnerOrgPage slug="highlander" /><Footer /></>} />
      <Route path="/socm" element={<><Navbar /><PartnerOrgPage slug="socm" /><Footer /></>} />
      <Route path="/tn-ballots" element={<><Navbar /><TNBallotsPage /><Footer /></>} />
      <Route path="/thornton-for-tennessee" element={<><Navbar /><ThorntonForTennessee /><Footer /></>} />
      <Route path="/thornton" element={<><Navbar /><ThorntonForTennessee /><Footer /></>} />
      <Route path="/nan-middle-tn" element={<><Navbar /><PartnerOrgPage slug="nan-middle-tn" /><Footer /></>} />
      <Route path="/equity-alliance" element={<><Navbar /><EquityAlliancePage /><Footer /></>} />
      <Route path="/naacp" element={<><Navbar /><NAACPPage /><Footer /></>} />
      <Route path="/naacp-desoto" element={<><Navbar /><NAACPDesotoPage /><Footer /></>} />
      <Route path="/micah-memphis" element={<><Navbar /><PartnerOrgPage slug="micah-memphis" /><Footer /></>} />
      <Route path="/sterling-institute" element={<><Navbar /><PartnerOrgPage slug="sterling-institute" /><Footer /></>} />
      <Route path="/metamorphosis-project" element={<><Navbar /><PartnerOrgPage slug="metamorphosis-project" /><Footer /></>} />
      <Route path="/community-development-accelerator" element={<><Navbar /><PartnerOrgPage slug="community-development-accelerator" /><Footer /></>} />
      <Route path="/whitehaven-youth" element={<><Navbar /><PartnerOrgPage slug="whitehaven-youth" /><Footer /></>} />
      <Route path="/ffun" element={<><Navbar /><PartnerOrgPage slug="ffun" /><Footer /></>} />
      <Route path="/carlissa-shaw" element={<><Navbar /><PartnerOrgPage slug="carlissa-shaw" /><Footer /></>} />
      <Route path="/legal-partners" element={<><Navbar /><LegalPartnersPage /><Footer /></>} />
      <Route path="/memphis-lift" element={<><Navbar /><PartnerOrgPage slug="memphis-lift" /><Footer /></>} />
      <Route path="/lye-academy" element={<><Navbar /><PartnerOrgPage slug="lye-academy" /><Footer /></>} />
      <Route path="/mvahs" element={<><Navbar /><PartnerOrgPage slug="mvahs" /><Footer /></>} />
      <Route path="/eeog" element={<><Navbar /><PartnerOrgPage slug="eeog" /><Footer /></>} />
      <Route path="/wlok" element={<><Navbar /><PartnerOrgPage slug="wlok" /><Footer /></>} />
      <Route path="/voice-of-mscs" element={<Navigate to="/mscs#voice" replace />} />
      <Route path="/wojg" element={<><Navbar /><PartnerOrgPage slug="wojg" /><Footer /></>} />
      <Route path="/ncnw" element={<><Navbar /><PartnerOrgPage slug="ncnw" /><Footer /></>} />
      <Route path="/dawg-team" element={<><Navbar /><PartnerOrgPage slug="dawg-team" /><Footer /></>} />
      <Route path="/elam" element={<><Navbar /><PartnerOrgPage slug="elam-home-improvement" /><Footer /></>} />
      <Route path="/elam-flyer" element={<ElamFlyer />} />
      <Route path="/angela-burton" element={<><Navbar /><AngelaBurtonPage /><Footer /></>} />
      <Route path="/parent-power" element={<><Navbar /><ParentPowerPage /><Footer /></>} />
      <Route path="/slingshot" element={<><Navbar /><PartnerOrgPage slug="slingshot" /><Footer /></>} />
      <Route path="/tcat-memphis" element={<><Navbar /><PartnerOrgPage slug="tcat-memphis" /><Footer /></>} />
      <Route path="/midsouth-dd" element={<><Navbar /><PartnerOrgPage slug="midsouth-dd" /><Footer /></>} />
      <Route path="/african-art" element={<><Navbar /><PartnerOrgPage slug="african-art" /><Footer /></>} />
      <Route path="/ethnik-vibes" element={<><Navbar /><PartnerOrgPage slug="ethnik-vibes" /><Footer /></>} />
      <Route path="/regional-one" element={<><Navbar /><PartnerOrgPage slug="regional-one" /><Footer /></>} />
      <Route path="/regional-one/employees" element={<><Navbar /><RegionalOneEmployeesPage /><Footer /></>} />
      <Route path="/regional-one/patients" element={<><Navbar /><RegionalOnePatientsPage /><Footer /></>} />
      <Route path="/ymca-memphis" element={<><Navbar /><PartnerOrgPage slug="ymca-memphis" /><Footer /></>} />
      <Route path="/wtls" element={<><Navbar /><PartnerOrgPage slug="wtls" /><Footer /></>} />
      <Route path="/united-benefits" element={<><Navbar /><PartnerOrgPage slug="united-benefits" /><Footer /></>} />
      <Route path="/muse-foundation" element={<><Navbar /><PartnerOrgPage slug="muse-foundation" /><Footer /></>} />
      <Route path="/property-taxes" element={<><Navbar /><PartnerOrgPage slug="property-assessor" /><Footer /></>} />
      <Route path="/libraries" element={<><Navbar /><LibrariesPage /><Footer /></>} />
      <Route path="/free-miracle" element={<><Navbar /><FreeMiracleArticlePage /><Footer /></>} />
      <Route path="/one-shelby-story" element={<><Navbar /><OneShelbyStoryPage /><Footer /></>} />
      <Route path="/feed-the-needy" element={<><Navbar /><FeedTheNeedyPage /><Footer /></>} />
      <Route path="/driving-the-dream" element={<><Navbar /><DrivingTheDreamPage /><Footer /></>} />
      <Route path="/build-with-us" element={<><Navbar /><BradshawSolutionsPage /><Footer /></>} />
      <Route path="/iptv" element={<><Navbar /><IPTVPage /><Footer /></>} />
      <Route path="/naalc" element={<><Navbar /><NAALCPage /><Footer /></>} />
      <Route path="/nmemca" element={<><Navbar /><PartnerOrgPage slug="nmemca" /><Footer /></>} />
      <Route path="/labor-and-ai" element={<><Navbar /><LaborAndAIPage /><Footer /></>} />
      <Route path="/pathways" element={<><Navbar /><PathwaysPage /><Footer /></>} />
      <Route path="/vonda-mcdaniel" element={<><Navbar /><VondaMcDanielPage /><Footer /></>} />
      <Route path="/vonda-school" element={<><Navbar /><VondaSchoolPage /><Footer /></>} />
      <Route path="/vonda-video-upload" element={<><Navbar /><VondaVideoUploadPage /><Footer /></>} />
      <Route path="/vonda-fellows" element={<><Navbar /><VondaFellowsPage /><Footer /></>} />
      <Route path="/vonda-fellows-admin" element={<VondaFellowsAdminPage />} />
      <Route path="/workers-interfaith" element={<><Navbar /><WorkersInterfaithPage /><Footer /></>} />
      <Route path="/win-fundraising" element={<><Navbar /><WINFundraisingCourse /><Footer /></>} />
      <Route path="/win-know-your-rights" element={<><Navbar /><WINWorkerRightsCourse /><Footer /></>} />
      <Route path="/win-org-growth" element={<><Navbar /><WINOrgGrowthCourse /><Footer /></>} />
      <Route path="/win-facilitator-kit" element={<><Navbar /><WINFacilitatorKit /><Footer /></>} />
      <Route path="/i-am-a-man/admin" element={<CivilRightsMuseumAdmin />} />
      <Route path="/back-to-work/admin" element={<BackToWorkAdmin />} />
      <Route path="/labor-pulpit-admin" element={<LaborPulpitAdmin />} />
      <Route path="/learn/financial-literacy" element={<><Navbar /><FinancialLiteracyPage /><Footer /></>} />
      <Route path="/endorsements" element={<><Navbar /><EndorsementsPage /><Footer /></>} />
      <Route path="/service-employees" element={<><Navbar /><ConventionBanner /><SEIUPage /><Footer /></>} />
      <Route path="/30th-convention" element={<Convention30 />} />
      <Route path="/convention" element={<Convention30 />} />
      <Route path="/bctgm-2026-convention" element={<BCTGM2026Convention />} />
      <Route path="/bctgm-convention" element={<BCTGM2026Convention />} />
      <Route path="/bctgm-international" element={<><Navbar /><BCTGMInternational /><Footer /></>} />
      <Route path="/central-labor-councils" element={<><Navbar /><CLCDirectory /><Footer /></>} />
      <Route path="/chat" element={<><Navbar /><AIChat /><Footer /></>} />
      <Route path="/share" element={<><Navbar /><SharePage /><Footer /></>} />
      <Route path="/giveaway" element={<><Navbar /><GiveawayPage /><Footer /></>} />
      <Route path="/local84" element={<><Navbar /><Local84Portal /><Footer /></>} />
      <Route path="/local84-admin" element={<><Navbar /><Local84Admin /><Footer /></>} />
      <Route path="/local252g-admin" element={<><Navbar /><Local252GAdmin /><Footer /></>} />
      <Route path="/local3g-admin" element={<><Navbar /><Local3GAdmin /><Footer /></>} />
      <Route path="/local3866-admin" element={<><Navbar /><Local3866Admin /><Footer /></>} />
      <Route path="/notifications-admin" element={<><Navbar /><NotificationsAdmin /><Footer /></>} />
      <Route path="/surveys-admin" element={<><Navbar /><SurveysAdmin /><Footer /></>} />
      <Route path="/vote/:surveyId" element={<><Navbar /><VotePage /><Footer /></>} />
      <Route path="/analytics" element={<AnalyticsDashboard />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="*" element={<UnknownRoute />} />
    </Routes>
    </Suspense>
    </div>
    </>
  );
}

const NO_SPLASH_PATHS = ["/malik", "/252g-grievance"];
const NO_SPLASH_PREFIXES = ["/safety", "/chem-inventory"];

function RouteAwareSplash({ show, onComplete }) {
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, "");
  if (!show || NO_SPLASH_PATHS.includes(path) || NO_SPLASH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return null;
  return <SplashScreen onComplete={onComplete} />;
}

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    const seen = sessionStorage.getItem('splashSeen');
    if (["/malik", "/252g-grievance"].includes(window.location.pathname.replace(/\/+$/, ''))) return false;
    return !seen;
  });

  const handleSplashComplete = React.useCallback(() => {
    setShowSplash(false);
    sessionStorage.setItem('splashSeen', 'true');
  }, []);

  React.useEffect(() => { recordDailyActivity(); }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <MusicPlayerProvider>
          <AppUpdateBanner />
          {showSplash && <RouteAwareSplash show={showSplash} onComplete={handleSplashComplete} />}
          <AppRouter />
          <MiniPlayer />
          <Toaster position="top-right" richColors />
        </MusicPlayerProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
